import { Actions, redirect as redirectBase, type TriggerRedirectArgs } from "@client/actions";
import { redirectTo as rt } from "@client/content/lib/settings";
import { apiAction, asyncAction, type ApiActionArgs } from "@client/lib/actions";
import { getCompositeSettingSelector } from "@client/selectors";
import { type BaseAppState, type CompositeSettings, type EntityRef } from "@client/types";
import type { ReduxAction } from "@client/types/ReduxAction";
import appInfo from "@common/appInfo";
import type { WithId } from "@common/declarations";
import { type Dispatch } from "redux";
import type { ThunkDispatch } from "redux-thunk";

type RedirectArgs = EntityRef &
  TriggerRedirectArgs & {
    prefix?: string;
    target: string;
  };
export const redirect =
  ({ prefix, target, ...args }: RedirectArgs) =>
  async (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const setting = getCompositeSettingSelector<CompositeSettings.Redirect>(
      target,
      "redirect",
      prefix
    )(getState(), args);

    return dispatch(
      redirectFromSettings({
        ...args,
        setting
      })
    );
  };

export type RedirectFromSettingsArgs = TriggerRedirectArgs & {
  setting: CompositeSettings.Redirect | null | undefined;
};
export const redirectFromSettings =
  ({ setting, ...args }: RedirectFromSettingsArgs) =>
  async (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const redirectTo = rt.createFromSetting(setting, state.funnelInfo, state.viewport.screen);
    dispatch(
      redirectBase({
        ...args,
        redirectTo
      })
    );
  };

export type InitActionArgs<TState, TArgs, TResponse, TResult = TResponse> = ApiActionArgs<
  TState,
  TArgs,
  TResponse,
  TResult
> & {
  getShouldInit?: (state: TState, args: TArgs) => boolean;
};
export const initAction =
  <TState, TArgs extends WithId, TResponse, TResult = TResponse>({
    getShouldInit,
    ...args
  }: InitActionArgs<TState, TArgs, TResponse, TResult>) =>
  (params: TArgs) =>
  (dispatch: ThunkDispatch<TState, TArgs, ReduxAction<any>>, getState: () => TState) => {
    const shouldInit =
      getShouldInit && (appInfo.user?.isStaff || appInfo.user?.isSuperUser) ? getShouldInit(getState(), params) : true;

    if (shouldInit) {
      dispatch(apiAction<TState, TArgs, TResponse, TResult>(args)(params));
    } else {
      dispatch({ type: Actions.SetModulePreview, data: { id: params.id } });
    }
  };
export const initAsyncAction = asyncAction;
